/* eslint-disable */
import {
  Button,
  Card,
  Form,
  Image,
  Input,
  message,
  Modal,
  Radio,
  Select,
  Space,
  Steps,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import api from "@/utils/api";
import http from "@/utils/http";
import commonFn from "@/utils/commonFn";
import { formatAmount } from "@/components/formatter";
import FormattedDate from "./FormattedDate";

/* ====================== 常量 & 工具 ====================== */
const VENDOR_MAINTENANCE = 1;
const VENDOR_CONTRACTOR = 2;
const VENDOR_TYPE_PARAM = { 1: "Maintenance", 2: "Contractor" };
const REQUEST_BY = { 1: "Tenant", 2: "Manager" };

const validateMessages = {
  required: "${label} is required!",
  types: {
    email: "${label} is not a valid email!",
    number: "${label} is not a valid number!",
  },
};

export const isOk = (res) => String(res?.code) === "1";
export const getAccountId = () => localStorage.getItem("accountId");
const fmt = (t) => (t ? moment(t).format("MM/DD/YYYY HH:mm") : "");

// 从 URL 中取工单 id(兼容 ?id=1 和 ?1 两种写法)
export const getMaintenanceId = (search) => {
  const hashQuery = window.location.hash.includes("?")
    ? window.location.hash.split("?")[1]
    : "";
  const query =
    (search || "").replace(/^\?/, "") ||
    hashQuery ||
    window.location.search.slice(1);
  if (!query) return "";
  return query.includes("=")
    ? new URLSearchParams(query).get("id") || ""
    : query;
};

// 根据接口数据推导“已完成到第几步”,不再用一堆 state 手动同步
const getStepShow = (d) => {
  if (d?.one?.processComplete !== 1) return 0;
  if (d.five?.processComplete === 1) return 5;
  if (d.four?.processComplete === 1) return 4;
  if (d.three?.processComplete === 1) return 3;
  if (d.two?.processComplete === 1) return 2;
  return 1;
};

/* ====================== 通用小组件 ====================== */
const PhotoGrid = ({ files = [], size = 100 }) =>
  files?.length > 0 ? (
    <Image.PreviewGroup>
      <div className="photo_grid">
        {files.map((f, i) => (
          <Image
            key={f.id ?? i}
            width={size}
            height={size}
            style={{ objectFit: "cover" }}
            src={f.url}
            alt={f.fileName}
          />
        ))}
      </div>
    </Image.PreviewGroup>
  ) : null;

const StepTitle = ({ title, parts = [] }) => (
  <Space size="middle" wrap>
    <strong>{title}</strong>
    {parts.filter(Boolean).map((p, i) => (
      <span key={i} className="stepHead">
        {p}
      </span>
    ))}
  </Space>
);

const DetailLine = ({ label, children, danger }) => (
  <div className={`detail_line${danger ? " text_danger" : ""}`}>
    <span className="detail_title">
      <strong>{label}&nbsp;</strong>
    </span>
    <span>{children}</span>
  </div>
);

const ApproveMark = ({ text }) => (
  <div className="detail_line text_success">
    <CheckCircleOutlined />
    <span style={{ marginLeft: 10 }}>{text || ""}</span>
  </div>
);

const Waiting = ({ text }) => (
  <div className="flexStart colYe">
    <ExclamationCircleOutlined className="iconYe" />
    <div>{text}</div>
  </div>
);

/* ====================== 第 1 步 ====================== */
function StepRequest({ one, maintenance }) {
  const propUnit =
    [maintenance.propUnit, maintenance.address].filter(Boolean).join("-") ||
    "No Data";
  return (
    <>
      <PhotoGrid files={one.maintenanceFiles} />
      <DetailLine label="Request Number:">{one.requestNo || "No Data"}</DetailLine>
      <DetailLine label="Prop-unit:">{propUnit}</DetailLine>
      <DetailLine label="City:">{maintenance.city || "No Data"}</DetailLine>
      <DetailLine label="Jobs:">{maintenance.job || "No Data"}</DetailLine>
      <div className="detail_line_title">
        <span className="detail_title">
          <strong>Remark:&nbsp;</strong>
        </span>
        <span className="detail_text">{maintenance.remark || "No Data"}</span>
      </div>
    </>
  );
}

/* ====================== 第 3 步:分配供应商表单 ====================== */
function AssignVendorForm({ submitting, onSubmit }) {
  const [form] = Form.useForm();
  const vendorType = Form.useWatch("vendorType", form);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const fetchVendors = async (keyword = "", type) => {
    const typeParam = VENDOR_TYPE_PARAM[type];
    if (!typeParam) return;
    const current = ++reqId.current; // 防止慢请求覆盖新结果
    setLoading(true);
    try {
      const res = await http.get(
        `${api.vendorType}?content=${encodeURIComponent(keyword)}&vendorType=${typeParam}`,
      );
      if (current !== reqId.current) return;
      const list = Array.isArray(res?.data) ? res.data : [];
      setOptions(
        list.map((i) => ({ label: i.vendorName, value: Number(i.vendorId) })),
      );
    } catch (e) {
      console.error(e);
    } finally {
      if (current === reqId.current) setLoading(false);
    }
  };

  const handleTypeChange = (e) => {
    const type = e.target.value;
    form.setFieldsValue({ vendorId: undefined, amount: undefined });
    setOptions([]);
    fetchVendors("", type);
  };

  const handleSearch = (value) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(
      () => fetchVendors(value, form.getFieldValue("vendorType")),
      300,
    );
  };

  return (
    <Form
      form={form}
      onFinish={onSubmit}
      validateMessages={validateMessages}
      labelCol={{ span: 5 }}
      wrapperCol={{ span: 19 }}
    >
      <Form.Item name="vendorType" label="Type" rules={[{ required: true }]}>
        <Radio.Group onChange={handleTypeChange}>
          <Radio value={VENDOR_MAINTENANCE}>Monthly Maintenance</Radio>
          <Radio value={VENDOR_CONTRACTOR}>Contractor(PO)</Radio>
        </Radio.Group>
      </Form.Item>

      {vendorType && (
        <Form.Item
          name="vendorId"
          label="Vendor"
          rules={[{ required: true, message: "Please select a vendor!" }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="Select Vendor"
            filterOption={false}
            notFoundContent={loading ? "Loading..." : "No vendors found"}
            options={options}
            loading={loading}
            style={{ width: "50%", minWidth: 200 }}
            onSearch={handleSearch}
            getPopupContainer={() => document.body}
          />
        </Form.Item>
      )}

      {vendorType === VENDOR_CONTRACTOR && (
        <Form.Item
          name="amount"
          label="Amount"
          rules={[
            { required: true, message: "Please input the amount!" },
            {
              pattern: /^\d+(\.\d{1,2})?$/,
              message: "Please input a valid amount!",
            },
          ]}
        >
          <Input inputMode="decimal" style={{ width: "50%", minWidth: 200 }} />
        </Form.Item>
      )}

      {vendorType && (
        <Form.Item
          name="sendEmail"
          label="Email Vendor"
          rules={[{ required: true, message: "Please select Yes or No" }]}
        >
          <Radio.Group>
            <Radio value={2}>No</Radio>
            <Radio value={1}>Yes</Radio>
          </Radio.Group>
        </Form.Item>
      )}

      <Form.Item wrapperCol={{ span: 24 }} className="btnCenter">
        <Button type="primary" htmlType="submit" loading={submitting}>
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
}

function StepAssign({ data, stepShow, submitting, onSubmit }) {
  const { two, three } = data;

  if (stepShow > 2 && three) {
    return (
      <>
        <DetailLine label="Vendor Type:">
          {three.vendorType === VENDOR_CONTRACTOR
            ? "Contractor(PO)"
            : "Monthly Maintenance"}
        </DetailLine>
        <DetailLine label="Selected Vendor:">{three.vendorName}</DetailLine>
        {three.vendorAmount ? (
          <DetailLine label="Quotation:">
            {formatAmount(three.vendorAmount)}
          </DetailLine>
        ) : null}
        <DetailLine label="Email Vendor:">
          {three.sendEmail === 2 ? "No" : "Yes"}
          {three.toEmail ? `, ${three.toEmail}` : ""}
        </DetailLine>
      </>
    );
  }

  if (two?.repairType === 2) {
    return (
      <div className="text_muted">
        This request is marked as <strong>Document Only</strong>, no vendor
        assignment is required.
      </div>
    );
  }

  if (two?.repairType === 1 && stepShow === 2) {
    return <AssignVendorForm submitting={submitting} onSubmit={onSubmit} />;
  }
  return null;
}

/* ====================== 第 4 步 ====================== */
function RepairInfo({ four }) {
  const needMaterial = four?.isMaterials;
  const rows = [
    four?.vendorAmount != null && ["Amount:", formatAmount(four.vendorAmount)],
    [
      "Predict Completion Time:",
      four?.predictCompletionTime
        ? moment(four.predictCompletionTime).format("MM/DD/YYYY")
        : "",
    ],
    ["Repair Remark:", four?.repairRemark || ""],
    ["Need Material:", { 1: "Yes", 2: "No" }[needMaterial] || ""],
    // 不需要材料(isMaterials === 2)时隐藏下面 3 项
    ...(needMaterial !== 2
      ? [
          ["Materials Remark:", four?.materialsRemark || ""],
          ["Pick-Up Location:", four?.storeName || ""],
          [
            "Materials Confirm Type:",
            { 1: "Agree", 2: "Refuse" }[four?.materialsConfirmType] || "",
          ],
        ]
      : []),
  ].filter(Boolean);

  return rows.map(([label, value]) => (
    <DetailLine key={label} label={label}>
      {value}
    </DetailLine>
  ));
}

function StepApproval({ data, onExamine }) {
  const { three, four } = data;

  if (three?.processComplete === 1 && !four) {
    return <Waiting text="Waiting For Vendor Submission" />;
  }
  if (!four) return null;

  const canExamine =
    four.processComplete === 2 && four.materialsConfirmType !== 2;

  return (
    <>
      <PhotoGrid files={four.maintenanceFiles} />
      <RepairInfo four={four} />
      {four.materialsConfirmType === 2 && (
        <DetailLine label="Refuse Reason:" danger>
          {four.refuseMaterialsRemark || ""}
        </DetailLine>
      )}
      {four.processComplete === 1 && <ApproveMark text={four.fourApproveRemark} />}
      {canExamine && (
        <Button type="primary" onClick={onExamine}>
          Examine
        </Button>
      )}
    </>
  );
}

/* ====================== 第 5 步 ====================== */
function StepOutcome({ data, onExamine }) {
  const { four, five } = data;

  if (four?.processComplete === 1 && !five) {
    return <Waiting text="Waiting For Repair Outcome" />;
  }
  if (!five) return null;

  const byDate = Object.entries(five.maintenanceFilesByDate || {});
  const tenantFiles = five.tenantConfirmFiles || [];
  const canExamine =
    five.repairCompleteType === 1 &&
    five.processComplete !== 1 &&
    five.repairCompleteConfirmType !== 2;

  return (
    <>
      {byDate.length === 0 && <PhotoGrid files={five.maintenanceFiles} />}
      {byDate.map(([date, files]) => (
        <div key={date} className="photo_group">
          <div className="photo_group_title">
            <span>Photo Uploaded On:&nbsp;</span>
            <FormattedDate dateString={date} />
          </div>
          <PhotoGrid files={files} />
        </div>
      ))}

      {tenantFiles.length > 0 && (
        <div className="detail_line">
          <strong>Job Accepted By: </strong>
          <span>
            {[five.tenantConfirmName, fmt(five.tenantConfirmTime)]
              .filter(Boolean)
              .join(", ")}
          </span>
          <PhotoGrid files={tenantFiles.slice(0, 1)} />
        </div>
      )}

      {five.repairCompleteConfirmType === 2 && (
        <DetailLine label="Refuse Reason:" danger>
          {five.refuseRemark || ""}
        </DetailLine>
      )}
      {five.repairCompleteConfirmType === 1 && (
        <ApproveMark text={five.fiveApproveRemark} />
      )}

      {canExamine && (
        <div className="mt10">
          <Button type="primary" onClick={onExamine}>
            Examine
          </Button>
        </div>
      )}
    </>
  );
}

/* ====================== 弹窗 ====================== */
// 第 4 / 5 步的 Agree / Refuse 审批弹窗
function ApprovalModal({ step, submitting, onCancel, onSubmit }) {
  const [form] = Form.useForm();
  const selection = Form.useWatch("selection", form);

  return (
    <Modal
      title="Do you agree?"
      open={step !== null}
      footer={null}
      centered
      width={480}
      onCancel={submitting ? undefined : onCancel}
      afterClose={() => form.resetFields()}
    >
      <Form
        form={form}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 17 }}
        validateMessages={validateMessages}
        onFinish={onSubmit}
      >
        <Form.Item
          label="Select"
          name="selection"
          rules={[{ required: true, message: "Please select an option!" }]}
        >
          <Radio.Group>
            <Radio value={1}>Agree</Radio>
            <Radio value={2}>Refuse</Radio>
          </Radio.Group>
        </Form.Item>

        {selection === 2 && (
          <Form.Item
            label="Refuse Reason"
            name="reason"
            rules={[{ required: true, message: "Please provide a reason!" }]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        )}

        <Form.Item wrapperCol={{ span: 24 }} className="btnCenter">
          <Space size="large">
            <Button size="large" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitting}
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}

/* ====================== 第 2 步确认弹窗 ====================== */
function RepairDecisionModal({
  open,
  repairType,
  reason,
  onReasonChange,
  submitting,
  onOk,
  onCancel,
}) {
  return (
    <Modal
      title={
        repairType === 1
          ? "Are you sure you want to choose Repair?"
          : "Are you sure you want to choose Document Only?"
      }
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      okText="Submit"
      confirmLoading={submitting}
      cancelButtonProps={{ disabled: submitting }}
      centered
    >
      {repairType === 2 && (
        <Input
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="Input Document Only Reason."
          maxLength={500}
          autoFocus
          disabled={submitting}
        />
      )}
    </Modal>
  );
}

/* ====================== 流程步骤(Details / NewOrder 共用) ======================
 * props:
 *   maintenanceId   工单 id(新建前为空)
 *   processData     api.process 返回的 data(新建前为 null)
 *   maintenance     工单详情
 *   onRefresh       操作成功后刷新流程数据,需返回 Promise
 *   requestContent  可选,覆盖第 1 步内容(NewOrder 用它放新建表单)
 */
export default function JobSteps({
  maintenanceId,
  processData,
  maintenance,
  onRefresh,
  requestContent,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [repairModalOpen, setRepairModalOpen] = useState(false);
  const [serviceType, setServiceType] = useState(null);
  const [documentReason, setDocumentReason] = useState("");
  const [approvalStep, setApprovalStep] = useState(null); // null | 4 | 5

  const data = processData || {};
  const { one, two, three, four, five } = data;
  const stepShow = getStepShow(processData);

  const postStep = async (url, payload, successText) => {
    const res = await http.post(url, payload);
    if (isOk(res)) {
      message.success(successText);
      await onRefresh?.();
      return true;
    }
    commonFn.modalFailedFn(res);
    return false;
  };

  /* 第 2 步 */
  const handleServiceChange = (e) => {
    setServiceType(e.target.value);
    setRepairModalOpen(true);
  };

  const closeRepairModal = () => {
    setRepairModalOpen(false);
    setDocumentReason("");
  };

  const submitRepairDecision = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const ok = await postStep(
        api.addTo2,
        {
          repairType: serviceType,
          maintenanceId,
          processType: 2,
          createBy: getAccountId(),
          documentOnlyReason: serviceType === 2 ? documentReason.trim() : "",
        },
        serviceType === 1 ? "Selected Repair" : "Selected Document Only",
      );
      if (ok) closeRepairModal();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  /* 第 3 步 */
  const submitAssign = async (values) => {
    setSubmitting(true);
    try {
      await postStep(
        api.addTo3,
        {
          maintenanceId,
          processType: 3,
          createBy: getAccountId(),
          vendorType: values.vendorType,
          vendorId: values.vendorId,
          sendEmail: values.sendEmail,
          vendorAmount:
            values.vendorType === VENDOR_CONTRACTOR ? values.amount || null : null,
        },
        "Submitted successfully",
      );
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  /* 第 4 / 5 步审批 */
  const submitApproval = async ({ selection, reason }) => {
    const isStep4 = approvalStep === 4;
    const agreeType = Number(selection);
    const refuseText = agreeType === 2 ? (reason || "").trim() : "";
    const base = {
      id: isStep4 ? four?.id : five?.id,
      maintenanceId,
      createBy: getAccountId(),
    };
    const payload = isStep4
      ? { ...base, materialsConfirmType: agreeType, refuseMaterialsRemark: refuseText }
      : { ...base, repairCompleteConfirmType: agreeType, refuseRemark: refuseText };

    setSubmitting(true);
    try {
      const ok = await postStep(
        isStep4 ? api.updateTo4 : api.updateTo5,
        payload,
        "Successfully Added",
      );
      if (ok) setApprovalStep(null);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  /* 渲染 */
  const card = (children) => <Card style={{ minWidth: 300 }}>{children}</Card>;

  const items = [
    {
      title: (
        <StepTitle
          title="Request Content"
          parts={
            stepShow > 0 && one
              ? [
                  one.createByName,
                  fmt(one.endTime),
                  REQUEST_BY[maintenance?.createByType] &&
                    `Request By: ${REQUEST_BY[maintenance.createByType]}`,
                ]
              : []
          }
        />
      ),
      description: card(
        requestContent ??
          (one && maintenance && (
            <StepRequest one={one} maintenance={maintenance} />
          )),
      ),
    },
    {
      title: (
        <StepTitle
          title="Repair Decision"
          parts={stepShow > 1 ? [two?.createByName, fmt(two?.endTime)] : []}
        />
      ),
      description: card(
        <Radio.Group
          onChange={handleServiceChange}
          value={two?.processComplete === 1 ? two.repairType : 0}
          disabled={stepShow !== 1} // 只有第 1 步完成、第 2 步未完成时可选
        >
          <Radio value={1}>
            <strong>Repair</strong>
          </Radio>
          <Radio value={2}>
            <strong>Document Only</strong>
          </Radio>
        </Radio.Group>,
      ),
    },
    {
      title: (
        <StepTitle
          title="Assign To"
          parts={stepShow > 2 ? [three?.createByName, fmt(three?.endTime)] : []}
        />
      ),
      description: card(
        <StepAssign
          data={data}
          stepShow={stepShow}
          submitting={submitting}
          onSubmit={submitAssign}
        />,
      ),
    },
    {
      title: (
        <StepTitle
          title="Approval Status"
          parts={stepShow > 3 ? [four?.createByName, fmt(four?.endTime)] : []}
        />
      ),
      description: card(
        <StepApproval data={data} onExamine={() => setApprovalStep(4)} />,
      ),
    },
    {
      title: (
        <StepTitle
          title="Repair Outcome"
          parts={stepShow > 4 ? [five?.createByName, fmt(five?.endTime)] : []}
        />
      ),
      description: card(
        <StepOutcome data={data} onExamine={() => setApprovalStep(5)} />,
      ),
    },
  ];

  return (
    <>
      <Steps
        direction="vertical"
        current={stepShow}
        labelPlacement="vertical"
        items={items}
      />

      <ApprovalModal
        step={approvalStep}
        submitting={submitting}
        onCancel={() => setApprovalStep(null)}
        onSubmit={submitApproval}
      />

      <RepairDecisionModal
        open={repairModalOpen}
        repairType={serviceType}
        reason={documentReason}
        onReasonChange={setDocumentReason}
        submitting={submitting}
        onOk={submitRepairDecision}
        onCancel={closeRepairModal}
      />
    </>
  );
}
